import * as React from 'react';
import { Check, ChevronsUpDown, Search, Users, X, Star, Clock, ArrowRight } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Client } from '@/types';
import { useCustomerSelector } from '@/hooks/use-customer-selector';
import { CustomerSearchModal } from './CustomerSearchModal';

interface EnhancedCustomerSelectProps {
  customers: Client[];
  selectedCustomerId?: string;
  onSelect: (customerId: string) => void;
  disabled?: boolean;
  placeholder?: string;
  className?: string;
}

export function EnhancedCustomerSelect({
  customers,
  selectedCustomerId,
  onSelect,
  disabled = false,
  placeholder = 'Select customer...',
  className,
}: EnhancedCustomerSelectProps) {
  const [open, setOpen] = React.useState(false);
  const [modalOpen, setModalOpen] = React.useState(false);
  const [searchQuery, setSearchQuery] = React.useState('');

  const {
    enrichedCustomers,
    favouriteCustomers,
    recentCustomers,
    toggleFavourite,
    recordCustomerAccess,
    isFavourite,
  } = useCustomerSelector(customers);

  const selectedCustomer = customers.find((c) => String(c.id) === String(selectedCustomerId));

  const filteredCustomers = React.useMemo(() => {
    if (!searchQuery) {
      return customers;
    }

    const query = searchQuery.toLowerCase();
    return customers.filter(
      (customer) =>
        customer.name.toLowerCase().includes(query) ||
        customer.industry.toLowerCase().includes(query) ||
        (customer.code || '').toLowerCase().includes(query)
    );
  }, [customers, searchQuery]);

  const sortedCustomers = React.useMemo(() => {
    return [...filteredCustomers].sort((a, b) => (a.name || '').localeCompare(b.name || ''));
  }, [filteredCustomers]);

  const handleSelect = (customerId: string) => {
    if (String(customerId) === String(selectedCustomerId)) {
      setOpen(false);
      setSearchQuery('');
      return;
    }
    onSelect(customerId);
    recordCustomerAccess(customerId);
    setOpen(false);
    setSearchQuery('');
  };

  const handleModalSelect = (customerId: string) => {
    if (String(customerId) === String(selectedCustomerId)) {
      setModalOpen(false);
      return;
    }
    onSelect(customerId);
    recordCustomerAccess(customerId);
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    onSelect('');
  };

  const popoverFavourites = favouriteCustomers.slice(0, 5);

  const popoverRecents = React.useMemo(() => {
    const favouriteIds = new Set(popoverFavourites.map(c => String(c.id)));
    return recentCustomers
      .filter(c => !favouriteIds.has(String(c.id)))
      .slice(0, 5);
  }, [recentCustomers, popoverFavourites]);

  return (
    <>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={open}
            disabled={disabled}
            className={cn(
              'w-full justify-start h-8 text-xs px-3',
              !selectedCustomer && 'text-muted-foreground',
              className
            )}
          >
            <div className="flex items-center gap-3 w-full">
              {selectedCustomer ? (
                <>
                  <div className="flex-1 min-w-0 text-left">
                    <span className="font-medium text-sm truncate block">
                      {selectedCustomer.name} ({selectedCustomer.code || selectedCustomer.id})
                    </span>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-8 w-8 p-0 shrink-0 hover:bg-destructive/10"
                    onClick={handleClear}
                    type="button"
                  >
                    <X className="h-4 w-4 text-muted-foreground hover:text-destructive transition-colors" />
                  </Button>
                </>
              ) : (
                <span className="flex-1 text-left">{placeholder}</span>
              )}
              <ChevronsUpDown className="h-4 w-4 shrink-0 opacity-50" />
            </div>
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[500px] p-0" align="start">
          <Command shouldFilter={false}>
            <div className="flex items-center border-b px-3">
              <Search className="mr-2 h-4 w-4 shrink-0 opacity-50" />
              <input
                placeholder="Search by name, code, or industry..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="flex h-11 w-full rounded-md bg-transparent py-3 text-sm outline-none placeholder:text-muted-foreground disabled:cursor-not-allowed disabled:opacity-50"
              />
            </div>

            <CommandList className="max-h-[400px]">
              {searchQuery && (
                <>
                  {sortedCustomers.length === 0 ? (
                    <div className="py-6 text-center">
                      <Users className="mx-auto h-8 w-8 text-muted-foreground/50 mb-2" />
                      <p className="text-sm text-muted-foreground">No customers found</p>
                      <p className="text-xs text-muted-foreground mt-1">
                        Try adjusting your search
                      </p>
                    </div>
                  ) : (
                    <CommandGroup>
                      <div className="flex items-center justify-between px-3 py-2 text-xs text-muted-foreground">
                        <span>{sortedCustomers.length} customer{sortedCustomers.length !== 1 ? 's' : ''} found</span>
                      </div>
                      {sortedCustomers.map((customer) => (
                        <CommandItem
                          key={String(customer.id)}
                          value={String(customer.id)}
                          onSelect={() => handleSelect(String(customer.id))}
                          className="px-3 py-3 cursor-pointer"
                        >
                          <div className="flex items-center gap-3 w-full">
                            <span className="font-medium text-sm truncate flex-1">
                              {customer.name} ({customer.code || customer.id})
                            </span>
                            <Check
                              className={cn(
                                'h-4 w-4 shrink-0',
                                String(selectedCustomerId) === String(customer.id) ? 'opacity-100' : 'opacity-0'
                              )}
                            />
                          </div>
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  )}
                </>
              )}

              {!searchQuery && (
                <>
                  {popoverFavourites.length > 0 && (
                    <CommandGroup>
                      <div className="flex items-center gap-2 px-3 py-2">
                        <Star className="h-3.5 w-3.5 text-yellow-500" />
                        <span className="text-xs font-semibold uppercase text-muted-foreground">Favourites</span>
                      </div>
                      {popoverFavourites.map((customer) => (
                        <CommandItem
                          key={`fav-${customer.id}`}
                          value={`fav-${customer.id}`}
                          onSelect={() => handleSelect(String(customer.id))}
                          className="px-3 py-2 cursor-pointer"
                        >
                          <div className="flex items-center gap-3 w-full">
                            <Star
                              className="h-4 w-4 shrink-0 fill-yellow-400 text-yellow-400 cursor-pointer"
                              onClick={(e) => {
                                e.stopPropagation();
                                toggleFavourite(String(customer.id));
                              }}
                            />
                            <span className="font-medium text-sm truncate flex-1">
                              {customer.name} ({customer.code || customer.id})
                            </span>
                            <Check
                              className={cn(
                                'h-4 w-4 shrink-0',
                                String(selectedCustomerId) === String(customer.id) ? 'opacity-100' : 'opacity-0'
                              )}
                            />
                          </div>
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  )}

                  {popoverRecents.length > 0 && (
                    <CommandGroup>
                      <div className="flex items-center gap-2 px-3 py-2">
                        <Clock className="h-3.5 w-3.5 text-muted-foreground" />
                        <span className="text-xs font-semibold uppercase text-muted-foreground">Recently Used</span>
                      </div>
                      {popoverRecents.map((customer) => (
                        <CommandItem
                          key={`recent-${customer.id}`}
                          value={`recent-${customer.id}`}
                          onSelect={() => handleSelect(String(customer.id))}
                          className="px-3 py-2 cursor-pointer"
                        >
                          <div className="flex items-center gap-3 w-full">
                            <Star
                              className={cn(
                                'h-4 w-4 shrink-0 cursor-pointer transition-colors',
                                isFavourite(String(customer.id))
                                  ? 'fill-yellow-400 text-yellow-400'
                                  : 'text-muted-foreground hover:text-yellow-400'
                              )}
                              onClick={(e) => {
                                e.stopPropagation();
                                toggleFavourite(String(customer.id));
                              }}
                            />
                            <span className="font-medium text-sm truncate flex-1">
                              {customer.name} ({customer.code || customer.id})
                            </span>
                            <Check
                              className={cn(
                                'h-4 w-4 shrink-0',
                                String(selectedCustomerId) === String(customer.id) ? 'opacity-100' : 'opacity-0'
                              )}
                            />
                          </div>
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  )}

                  {!popoverFavourites.length && !popoverRecents.length && (
                    <div className="py-6 text-center">
                      <Users className="mx-auto h-8 w-8 text-muted-foreground/50 mb-2" />
                      <p className="text-sm text-muted-foreground">No favourites or recent customers</p>
                      <p className="text-xs text-muted-foreground mt-1">
                        Click "Show More..." to browse all customers
                      </p>
                    </div>
                  )}
                </>
              )}
            </CommandList>

            <div className="border-t p-2">
              <Button
                variant="ghost"
                className="w-full justify-center text-sm"
                onClick={() => {
                  setOpen(false);
                  setModalOpen(true);
                }}
              >
                <ArrowRight className="mr-2 h-4 w-4" />
                Show More...
              </Button>
            </div>
          </Command>
        </PopoverContent>
      </Popover>

      <CustomerSearchModal
        open={modalOpen}
        onOpenChange={setModalOpen}
        customers={enrichedCustomers}
        value={selectedCustomerId}
        onSelect={handleModalSelect}
        onToggleFavourite={toggleFavourite}
        isFavourite={isFavourite}
        selectedCustomerId={selectedCustomerId}
      />
    </>
  );
}
